import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { ReportsHeadset } from 'src/models';
import {
  getReportsHeadsetsAction,
  activateHeadsetSubscriptionAction,
  cancelHeadsetSubscriptionsAction,
} from 'src/services/reportHeadsets';

const headsetsEntityAdapter = createEntityAdapter({
  selectId: (headset: ReportsHeadset) => headset.id,
});

export const headsetsSelectors = headsetsEntityAdapter.getSelectors();

const initialState: {
  isPending: boolean | null;
  hasError: boolean;
} = {
  isPending: null,
  hasError: false,
};

const reportsHeadsetsSlice = createSlice({
  name: 'reports_headsets',
  initialState: headsetsEntityAdapter.getInitialState(initialState),
  reducers: {
    clearReportsHeadsetsState: (state) => {
      state.isPending = null;
      state.hasError = false;
      headsetsEntityAdapter.setAll(state, []);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getReportsHeadsetsAction.pending, (state) => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(
      getReportsHeadsetsAction.fulfilled,
      (state, { payload }) => {
        state.isPending = false;
        state.hasError = false;

        headsetsEntityAdapter.setAll(
          state,
          payload.map((headset) => ({
            ...headset,
            activeSubscriptions: headset.activeSubscriptions || [],
            companyPaymentMethods: headset.companyPaymentMethods || [],
          })),
        );
      },
    );
    builder.addCase(getReportsHeadsetsAction.rejected, (state) => {
      state.isPending = false;
      state.hasError = true;
    });

    builder.addCase(
      activateHeadsetSubscriptionAction.fulfilled,
      (state, { meta, payload }) => {
        const headset = headsetsSelectors.selectById(
          state,
          meta.arg.headsetId,
        ) as ReportsHeadset;
        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: {
            activeSubscriptions: [...headset.activeSubscriptions, payload],
          },
        });
      },
    );

    builder.addCase(
      cancelHeadsetSubscriptionsAction.fulfilled,
      (state, { meta }) => {
        headsetsEntityAdapter.updateOne(state, {
          id: meta.arg.headsetId,
          changes: { activeSubscriptions: [] },
        });
      },
    );
  },
});

export const { clearReportsHeadsetsState } = reportsHeadsetsSlice.actions;

const { reducer } = reportsHeadsetsSlice;

export default reducer;
